/**
 * Types — DedicatedServer (admin).
 *
 * Miroir de `apps/web/src/types/dedicatedServer.ts` (source de vérité
 * historique), enrichi avec les champs spec `APP-ADMIN-DEDICATED-SERVERS-001`
 * §2.3 (failure shape, capabilities, organization enrichi backend).
 *
 * @spec specs/app/admin/dedicated-servers.md §2.3
 */

// ============================================================================
// STATUS & PLAN
// ============================================================================

export type ServerStatus =
  | 'provisioning'
  | 'installing'
  | 'active'
  | 'failed'
  | 'suspended'
  | 'destroyed';

export type ServerPlan = 'starter' | 'pro' | 'team' | 'business' | 'enterprise';

export type ProvisioningStage =
  | 'unknown'
  | 'ssh'
  | 'apt_update'
  | 'docker'
  | 'sysbox'
  | 'redis'
  | 'tls'
  | 'docker_network'
  | 'workspace_images'
  | 'final_verification'
  | 'timeout'
  | 'hetzner_api'
  | 'cloud_init';

export type RecoveryOption = 'retry' | 'rebuild' | 'destroy' | 'contact_support';

// ============================================================================
// CAPABILITIES / FAILURE / METRICS
// ============================================================================

export interface ServerCapabilities {
  docker_ready: boolean;
  tls_ready: boolean;
  sysbox_ready: boolean;
  redis_ready: boolean;
  firewall_ready: boolean;
  workspaces_ready: boolean;
  log_streaming_ready: boolean;
}

export interface ServerFailure {
  stage: ProvisioningStage;
  stage_label_key: string;
  reason: string;
  occurred_at: string;
  logs_tail: string;
  recovery_options: RecoveryOption[];
}

export interface ServerSpecs {
  cpu: number;
  ram_gb: number;
  disk_gb: number;
}

export interface ServerMetricsSeriesPoint {
  timestamp: string;
  cpu: number;
  ram: number;
  disk: number;
}

export interface ServerMetrics {
  cpu_percent: number;
  ram_percent: number;
  disk_percent: number;
  bandwidth_tb?: number;
  series?: ServerMetricsSeriesPoint[];
}

// ============================================================================
// ENTITY
// ============================================================================

export interface DedicatedServer {
  id: string;
  organization_id: string;
  organization_name?: string;
  name: string;
  plan: ServerPlan;
  region: string;
  status: ServerStatus;
  ip_address: string | null;
  hetzner_server_id: number | null;
  hetzner_server_name?: string | null;
  specs: ServerSpecs;
  capabilities?: ServerCapabilities;
  failure?: ServerFailure | null;
  monthly_cost_eur: number;
  stripe_subscription_id?: string | null;
  created_at: string;
  last_activity_at: string;
  suspended_at?: string | null;
  destroyed_at?: string | null;
  custom_domain?: string | null;
}

// ============================================================================
// STATS
// ============================================================================

export interface DedicatedServerStats {
  total: number;
  installing: number;
  active: number;
  failed: number;
  suspended: number;
  destroyed: number;
  monthly_cost_eur_total: number;
  monthly_revenue_eur_total: number;
}

// ============================================================================
// PROVISIONING LOGS
// ============================================================================

export type ProvisioningLogLevel = 'info' | 'warn' | 'error' | 'debug';

export interface ProvisioningLog {
  id?: string;
  created_at: string;
  level: ProvisioningLogLevel | string;
  step?: string;
  output: string;
}

// ============================================================================
// WORKSPACES (sous-ressource)
// ============================================================================

export type WorkspaceStatus = 'running' | 'stopped' | 'starting' | 'failed' | 'destroyed';

export interface ServerWorkspace {
  id: string;
  name: string;
  status: WorkspaceStatus;
  owner?: { id: string; email: string; name?: string };
  project?: { id: string; name: string; organizationId?: string };
  created_at: string;
  last_activity_at: string;
}

// ============================================================================
// AUDIT LOG
// ============================================================================

export type AdminAction =
  | 'retry'
  | 'rebuild'
  | 'destroy'
  | 'suspend'
  | 'resume'
  | 'provision'
  | 'force_cancel';

export interface AdminActionLog {
  id: string;
  occurred_at: string;
  actor: { id: string; email: string; name?: string };
  action: AdminAction;
  reason: string;
  result: 'success' | 'failure';
  error_message?: string | null;
  target_stage?: ProvisioningStage | null;
}

// ============================================================================
// DISPLAY CONSTANTS (FR hard-codé — §9 Option A)
// ============================================================================

export const SERVER_PLAN_LABELS: Record<ServerPlan, string> = {
  starter: 'Starter',
  pro: 'Pro',
  team: 'Team',
  business: 'Business',
  enterprise: 'Enterprise',
};

export const SERVER_PLAN_SPECS: Record<
  ServerPlan,
  { cpu: number; ram_gb: number; disk_gb: number; maxWorkspaces: number; price: number }
> = {
  starter: { cpu: 2, ram_gb: 4, disk_gb: 40, maxWorkspaces: 1, price: 9 },
  pro: { cpu: 4, ram_gb: 8, disk_gb: 80, maxWorkspaces: 3, price: 29 },
  team: { cpu: 8, ram_gb: 16, disk_gb: 160, maxWorkspaces: 5, price: 59 },
  business: { cpu: 4, ram_gb: 16, disk_gb: 240, maxWorkspaces: 10, price: 99 },
  enterprise: { cpu: 8, ram_gb: 32, disk_gb: 480, maxWorkspaces: -1, price: 199 },
};

export const SERVER_STATUS_LABELS: Record<ServerStatus, string> = {
  provisioning: 'Provisioning',
  installing: 'Installation',
  active: 'Actif',
  failed: 'Échoué',
  suspended: 'Suspendu',
  destroyed: 'Détruit',
};

export const SERVER_STATUS_DESCRIPTIONS: Record<ServerStatus, string> = {
  provisioning: 'VM en cours de création chez Hetzner',
  installing: 'Bootstrap Docker / Sysbox / TLS en cours',
  active: 'Serveur opérationnel',
  failed: 'Setup en erreur, intervention admin requise',
  suspended: 'VM stoppée, données conservées',
  destroyed: 'VM supprimée chez Hetzner',
};

export const PROVISIONING_STAGE_LABELS_FR: Record<ProvisioningStage, string> = {
  unknown: 'Étape inconnue',
  ssh: 'Connexion SSH',
  apt_update: 'Mise à jour paquets APT',
  docker: 'Installation Docker',
  sysbox: 'Installation Sysbox',
  redis: 'Installation Redis',
  tls: 'Génération certificats TLS',
  docker_network: 'Configuration réseau Docker',
  workspace_images: 'Construction images workspace',
  final_verification: 'Vérification finale',
  timeout: 'Timeout',
  hetzner_api: 'API Hetzner Cloud',
  cloud_init: 'Cloud-init',
};

export const REGION_LABELS: Record<string, { label: string; country: string; flag: string }> = {
  nbg1: { label: 'Nuremberg', country: 'DE', flag: '🇩🇪' },
  hel1: { label: 'Helsinki', country: 'FI', flag: '🇫🇮' },
  fsn1: { label: 'Falkenstein', country: 'DE', flag: '🇩🇪' },
  ash: { label: 'Ashburn', country: 'US', flag: '🇺🇸' },
  hil: { label: 'Hillsboro', country: 'US', flag: '🇺🇸' },
  sin: { label: 'Singapore', country: 'SG', flag: '🇸🇬' },
};

export const CAPABILITY_LABELS_FR: Record<keyof ServerCapabilities, string> = {
  docker_ready: 'Docker installé',
  tls_ready: 'TLS configuré',
  sysbox_ready: 'Runtime Sysbox',
  redis_ready: 'Redis local',
  firewall_ready: 'Firewall configuré',
  workspaces_ready: 'Images workspace prêtes',
  log_streaming_ready: 'Streaming logs actif',
};

export const CAPABILITY_TOOLTIPS_FR: Record<keyof ServerCapabilities, string> = {
  docker_ready: "docker --version répond OK",
  tls_ready: "/etc/docker/certs/*.pem existants et valides",
  sysbox_ready: "sysbox-runc --version OK, daemon actif",
  redis_ready: "redis-cli ping répond PONG",
  firewall_ready: "ufw status = active avec règles attendues",
  workspaces_ready: "build-workspace-images.sh a terminé sans erreur",
  log_streaming_ready: "Redis Stream configuré, collector actif",
};
